import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import Styled from './style';
import { Button, Flex, Select, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useCrmIntegration } from '../../../../../../../../features/crm-integration/hooks/use-crm-integration';
import {
  SelectedProperty,
  WorkspaceCrmProperty,
} from '../../../../../../../../features/crm-integration/crm-integration.types';
import { useToast } from '../../../../../../../../hooks/useToast';
import { coreService } from '../../../../../../../../services/core/core-service';
import { useUserSettings } from '../../../../../../../../features/user/hooks/useUserSettings';
import ToggleCard from '../ToggleCard';

const { Title, CRMConnectedStatus, SyncNotice } = Styled;

export interface Props {
  disableUserInteractions: boolean;
  onSave: VoidFunction;
}

export const CRMOrganizationSection: React.FC<Props> = ({ disableUserInteractions, onSave }) => {
  const { workspaceCrmIntegration, isFetching } = useCrmIntegration();
  const { settings, updateSettings } = useUserSettings();

  const [selectedProperties, setSelectedProperties] = useState<SelectedProperty[]>([]);
  const [filterProperties, setFilterProperties] = useState<SelectedProperty[]>([]);
  const [availableProperties, setAvailableProperties] = useState<WorkspaceCrmProperty[]>([]);
  const [revenueValueProperty, setRevenueValueProperty] = useState<SelectedProperty | null>(null);

  const { success } = useToast();

  const [isSavingProperties, setIsSavingProperties] = useState(false);

  const hasCrmConnected = Boolean(workspaceCrmIntegration?.workspace.integration);

  const status = useMemo(() => {
    if (isFetching) {
      return {
        connected: false,
        status: 'Reading integration status',
      };
    }

    if (!hasCrmConnected) {
      return {
        connected: false,
        status: 'Not connected',
      };
    }

    if (workspaceCrmIntegration?.workspace.integration?.status === 'INVALID') {
      return {
        connected: true,
        status: 'IMPORTANT: Reconnect CRM. Credentials expired.',
      };
    }

    return {
      connected: true,
      status: 'Connected',
    };
  }, [hasCrmConnected, workspaceCrmIntegration, isFetching]);

  const crmProvider =
    workspaceCrmIntegration?.workspace.integration?.provider === 'salesforce'
      ? 'Salesforce'
      : 'HubSpot';

  useEffect(() => {
    if (workspaceCrmIntegration && workspaceCrmIntegration.workspace) {
      setSelectedProperties(workspaceCrmIntegration.workspace.selectedProperties);
      setAvailableProperties(workspaceCrmIntegration.workspace.properties);
      setFilterProperties(workspaceCrmIntegration.workspace.filterProperties);
      setRevenueValueProperty(workspaceCrmIntegration.workspace.revenueValueProperty);
    }
  }, [workspaceCrmIntegration]);

  const propertiesOptions = useMemo(() => {
    if (!hasCrmConnected) return [];
    return availableProperties.map(p => ({ label: p.label, value: p.id }));
  }, [availableProperties, hasCrmConnected]);

  const handleOptionClicked = (values: string[]) => {
    const validValues = values.filter(v => availableProperties.find(p => p.id === v) !== undefined);
    setSelectedProperties(validValues);
  };

  const handleFilterOptionClicked = (values: string[]) => {
    const validValues = values.filter(v => availableProperties.find(p => p.id === v) !== undefined);
    setFilterProperties(validValues);
  };

  const handleSave = () => {
    setIsSavingProperties(true);
    coreService
      .updateWorkspaceCrmIntegrationSelectedProperties({
        displayProperties: selectedProperties,
        filterProperties,
        revenueValueProperty: revenueValueProperty || '',
      })
      .then(() => {
        success('Account Properties saved successfully');
        onSave();
      })
      .finally(() => setIsSavingProperties(false));
  };

  const isPullingProperties = hasCrmConnected && availableProperties.length === 0;

  return (
    <Flex gap={12} vertical>
      <Title>
        CRM Integration Status:{' '}
        <CRMConnectedStatus connected={status.connected}>{status.status}</CRMConnectedStatus>
      </Title>

      {status.connected && (
        <SyncNotice>
          <InfoCircleOutlined /> Note: CRM account properties sync automatically every hour.
        </SyncNotice>
      )}

      {isPullingProperties ? (
        <Flex gap={4} vertical>
          <Title>
            Account properties list is being pulled from the CRM. Please refresh the page to update
            the status.
          </Title>
          <Title>
            Once this is finished, you'll be able to select which properties to display in Account
            Insights or use as filters in dashboards.
          </Title>
        </Flex>
      ) : !hasCrmConnected ? null : (
        <Flex vertical gap={16}>
          <Flex vertical gap={4}>
            <Title>
              {isPullingProperties && hasCrmConnected
                ? `Account properties list is being pulled from the CRM. Please refresh the page to update the status.`
                : 'Account properties to display'}
            </Title>

            <Select
              mode="tags"
              loading={isPullingProperties}
              disabled={!hasCrmConnected || isPullingProperties}
              onChange={value => handleOptionClicked(value)}
              filterOption={(input, option) => {
                return option?.label.toLowerCase().includes(input.toLowerCase()) || false;
              }}
              autoClearSearchValue={true}
              value={selectedProperties}
              options={propertiesOptions}
            />
          </Flex>

          <Flex vertical gap={4}>
            <Title>Account properties to use as filter</Title>

            <Select
              mode="tags"
              loading={isPullingProperties}
              disabled={!hasCrmConnected || isPullingProperties}
              filterOption={(input, option) => {
                return option?.label.toLowerCase().includes(input.toLowerCase()) || false;
              }}
              onChange={value => handleFilterOptionClicked(value)}
              autoClearSearchValue={true}
              value={filterProperties}
              options={propertiesOptions}
            />
          </Flex>

          <Flex vertical gap={4}>
            <Title>
              Property to assign as Commercial Impact of each Account.{' '}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Only currency properties are available.
              </Typography.Text>
            </Title>

            <Select
              loading={isPullingProperties}
              disabled={!hasCrmConnected || isPullingProperties}
              filterOption={(input, option) => {
                return option?.label.toLowerCase().includes(input.toLowerCase()) || false;
              }}
              onChange={(value: string) => setRevenueValueProperty(value)}
              autoClearSearchValue={true}
              value={
                workspaceCrmIntegration?.workspace.properties.find(
                  p => p.id === revenueValueProperty,
                )?.label
              }
              options={workspaceCrmIntegration?.workspace.properties
                .filter(property => property.rawType === 'currency')
                .map(p => ({ label: p.label, value: p.id }))}
            />
          </Flex>

          <div>
            <ToggleCard
              title={`Auto push meeting recaps to ${crmProvider}`}
              description={`Automatically push my meeting recaps to the ${crmProvider} account I have connected.`}
              recommendedSetting="ON"
              checked={settings.syncRecapAutomaticallyCrm}
              onChange={e => updateSettings({ syncRecapAutomaticallyCrm: !!e })}
            />
          </div>

          <Flex justify="center" style={{ width: '100%' }}>
            <Button
              disabled={!hasCrmConnected || isFetching || isSavingProperties}
              onClick={handleSave}
              type="primary"
            >
              Save Properties
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
